<script setup lang="ts">
import { HitStatus } from '@/types';
import { useResizeObserver } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';
import { useInjectGameEngine } from '../composables/useGameEngine';
import { useInjectSheetMusic } from '../composables/useSheetMusic';

const sheetMusic = useInjectSheetMusic();
const gameEngine = useInjectGameEngine();
const styles = getComputedStyle(document.documentElement);
const perspectiveContainer = ref<HTMLElement>();
const arcadeContainer = ref<HTMLElement>();
const perspectiveContainerWidth = ref(perspectiveContainer.value?.clientWidth);
const arcadeContainerWidth = ref(arcadeContainer.value?.clientWidth);

// Base time window calibration - this will be our reference width for normalizing time window
const baseWidth = ref(1000); // A reference screen width in pixels
const timeWindowNormalizationFactor = computed(() => {
  if (!perspectiveContainerWidth.value) return 1;
  // Calculate a normalization factor to keep speed consistent across screen sizes
  return Math.pow(perspectiveContainerWidth.value, 2) / Math.pow(baseWidth.value, 2);
});

useResizeObserver(perspectiveContainer, ()=> {
  perspectiveContainerWidth.value = perspectiveContainer.value?.clientWidth;
  arcadeContainerWidth.value = arcadeContainer.value?.clientWidth;
});

onMounted(()=>{
  perspectiveContainerWidth.value = perspectiveContainer.value?.clientWidth;
  arcadeContainerWidth.value = arcadeContainer.value?.clientWidth;
});

// The lanes for the notes to fall in (corresponding to different drums)
const lanes = ref([
    { name: 'Hi-Hat', midiNumbers: [22, 26, 42, 44, 46], color: styles.getPropertyValue('--color-arcade-lane-hihat').trim(), position: 0.2 },
    { name: 'Snare Drum', midiNumbers: [38], color: styles.getPropertyValue('--color-arcade-lane-snare').trim(), position: 0.4 },
    { name: 'Bass Drum', midiNumbers: [36], color: styles.getPropertyValue('--color-arcade-lane-bass').trim(), position: 0.6 },
    { name: 'Toms/Cymbals', midiNumbers: [41, 43, 47, 48, 49, 51, 55], color: styles.getPropertyValue('--color-arcade-lane-toms').trim(), position: 0.8 },
]);

// Calculate the visible notes based on the current time
const visibleNotes = computed(() => {
  if (!sheetMusic.notes.value.length) return [];
  
  const currentTime = gameEngine.currentTime.value;
  // Adjust time window based on screen width to keep speed consistent
  const futureTimeWindow = 5000 * timeWindowNormalizationFactor.value; 
  
  return gameEngine.currentGameNotes.value
    .filter(note => {
      // Show notes that are about to be hit or just missed
      const noteTime = note.timestamp;
      return noteTime > currentTime - 500 && noteTime < currentTime + futureTimeWindow;
    })
    .map(note => {
      // Determine which lane this note belongs to
      const laneIndex = lanes.value.findIndex(lane => 
        lane.midiNumbers.some(midi => note.possibleMidiNumbers && note.possibleMidiNumbers.includes(midi))
      );
      
      // If no matching lane, default to the last lane
      const lane = laneIndex >= 0 ? lanes.value[laneIndex] : lanes.value[lanes.value.length - 1];
      
      // Calculate horizontal position based on time difference with speed normalization
      const timeDiff = note.timestamp - currentTime;
      const horizontalPosition = 1 - (timeDiff / futureTimeWindow);

      return {
        ...note,
        lane,
        horizontalPosition,
      };
    });
});
</script>

<template>
  <div
    ref="perspectiveContainer"
    class="perspective-container w-full h-full" 
    :style="{
      transformStyle: 'preserve-3d',
      perspective: `800px`,
      perspectiveOrigin: `${80000/(perspectiveContainerWidth ?? 1)}% 50%`
    }"
  >
    <div 
      ref="arcadeContainer" 
      class="arcade-container w-full h-full relative overflow-hidden bg-background bg-opacity-90"
      :style="{
          transform: `translateZ(-${(arcadeContainerWidth ?? 0) / 4}px) rotateY(40deg) translateZ(-${(arcadeContainerWidth ?? 0) / 4}px)`,
          width: `${(perspectiveContainerWidth ?? 0)/5.5}%`
      }"
    >
      <!-- Target zone on the left -->
      <div class="target-zone absolute left-0 h-full flex flex-col justify-center items-center w-16">
        <div 
          v-for="lane in lanes" 
          :key="lane.name" 
          class="target-circle rounded-full border-2 flex items-center justify-center"
          :style="{ 
            top: `${lane.position * 100}%`, 
            borderColor: lane.color,
            width: '50px',
            height: '50px',
            transform: 'translateY(-50%)',
            position: 'absolute',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }"
        >
          <span class="text-xs text-text-primary opacity-70">{{ lane.name.split(' ')[0] }}</span>
        </div>
      </div>
      
      <!-- Note lanes -->
      <div class="absolute inset-0">
        <div 
          v-for="lane in lanes" 
          :key="lane.name" 
          class="lane absolute h-px"
          :style="{ 
            top: `${lane.position * 100}%`, 
            backgroundColor: `${lane.color}70`,
            width: '100%',
            color: lane.color,
          }"
        ></div>
      </div>
      
      <!-- Moving notes (right to left) -->
      <div class="notes-container absolute inset-0">
        <div 
          v-for="(note, index) in visibleNotes" 
          :key="`note-${note.noteName}-${note.timestamp}`"
          class="note absolute rounded-full flex items-center justify-center"
          :class="{ 
            'opacity-30': note.played, 
            'hit': note.hitStatus === HitStatus.Perfect || note.hitStatus === HitStatus.Good,
          }"
          :style="{ 
            top: `${note.lane.position * 100}%`, 
            left: `${(1 - note.horizontalPosition) * 100}%`, 
            backgroundColor: note.played ? `color-mix(in srgb, ${note.lane.color} 50%, ${styles.getPropertyValue('--color-arcade-note-played-mix').trim()})` : note.lane.color,
            width: '40px',
            height: '40px',
            transform: 'translate(50%, -50%)',
            transition: 'left 0s linear',
            boxShadow: note.played ? 'none' : `0 0 10px ${note.lane.color}`
          }"
        >
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Add 3D perspective to the container */
.perspective-container {
  transform-style: preserve-3d;
}

.arcade-container {
  width: 400%;
}

.target-circle {
  animation: pulse 2s infinite;
}

.note {
  animation: glow 1.5s infinite alternate;
  transition: left 0s linear, opacity 0.3s ease-out;
}

.note.hit {
  animation: hit-pulse 0.3s forwards;
}

@keyframes hit-pulse {
  0% {
    transform: translate(50%, -50%) scale(1);
    opacity: 1;
  }
  50% {
    transform: translate(50%, -50%) scale(1.5);
    opacity: 0.8;
  }
  100% {
    transform: translate(50%, -50%) scale(3);
    opacity: 0;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

@keyframes glow {
  0% {
    box-shadow: 0 0 5px currentColor;
  }
  100% {
    box-shadow: 0 0 20px currentColor;
  }
}

.lane {
  box-shadow: 0 0 15px 15px color-mix(in srgb, currentColor 20%, transparent);
  opacity: 1;
}

/* Add particle effect when a note is hit */
.note.played::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%);
  border-radius: 50%;
  animation: explode 0.5s forwards;
}

@keyframes explode {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}
</style> 
